// DeliveryLocation.js – Production Ready (Amazon-style)

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import CheckoutBreadcrumbs from "./CheckoutBreadcrumbs";
import MapPicker from "../components/Registration/MapPicker";
import api from "../api/axios";
import { getCheckout, setCheckout } from "../utils/buyNowStorage";


export default function DeliveryLocation() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [user, setUser] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);

  const [openAdd, setOpenAdd] = useState(false);
  const [newAddress, setNewAddress] = useState({
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
    addressType: "Home",
    otherType: "",
  });
  const [mapKey, setMapKey] = useState(0);
  const [addressError, setAddressError] = useState("");

  const checkout = getCheckout();

  /* ---------------- LOAD PRODUCT INTO CHECKOUT ---------------- */
  useEffect(() => {
    if (!id) return;

    const loadProduct = async () => {
      try {
        const res = await api.get(`/reels/${id}`);
        const product = res.data;

        setCheckout({
          mode: "BUY_NOW",
          items: [
            {
              id: product._id || product.id,
              title: product.title,
              price: Number(product.price) || 0,
              imageUrl: product.images?.[0]?.thumbnailKey || product.images?.[0] || product.imageUrl,
              qty: 1,
              vendorId: product.vendorId,
              vendorName: product.vendorName,
            },
          ],
        });
      } catch (err) {
        console.error("Failed to load product", err);
      }
    };

    loadProduct();
  }, [id]);

  /* ---------------- LOAD USER + ADDRESSES ---------------- */
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser?.username) return;

    setUser(storedUser);

    api
      .get(`/users/${storedUser.username}/addresses`)
      .then((res) => {
        const addresses = res.data.addresses || [];
        setSavedAddresses(addresses);

        const defaultAddr = addresses.find((a) => a.isDefault);
        if (defaultAddr) setSelectedAddressId(defaultAddr._id);
      })
      .catch(() => setSavedAddresses([]));
  }, []);

  /* ---------------- CONTINUE ---------------- */
  const handleContinue = () => {
    if (!selectedAddressId) {
      alert("Please select a delivery address");
      return;
    }

    if (checkout) {
      setCheckout({ ...checkout, addressId: selectedAddressId });
    }

    navigate("/order-summary", {
      state: { addressId: selectedAddressId },
    });
  };

  /* ---------------- SAVE ADDRESS ---------------- */
  const handleSaveAddress = async () => {
    const { addressLine1, city, state, pincode, addressType, otherType } = newAddress;
    if (!addressLine1 || !city || !state || !pincode) {
      setAddressError("Please fill all required address fields");
      return;
    }
    if (addressType === "Other" && !otherType) {
      setAddressError("Please specify the address type");
      return;
    }

    setAddressError("");

    try {
      const res = await api.post(
        `/users/${user.username}/addresses`,
        newAddress
      );
      setSavedAddresses(res.data.addresses || []);
      setOpenAdd(false);
      setMapKey((k) => k + 1);
      setNewAddress({
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        pincode: "",
        addressType: "Home",
        otherType: "",
      });
    } catch {
      setAddressError("Failed to save address");
    }
  };

  /* ---------------- MAP LOCATION SELECTED ---------------- */
  const handleLocationSelected = (location) => {
    setNewAddress((prev) => ({
      ...prev,
      location,
    }));
  };

  /* ---------------- MAP ADDRESS FILLED ---------------- */
  const handleAddressFilled = (fields) => {
    setNewAddress((prev) => ({
      ...prev,
      ...fields,
    }));
  };

  /* ---------------- UI ---------------- */
  return (
    <Box sx={{ maxWidth: 720, mx: "auto", px: 2, py: 3 }}>
      <CheckoutBreadcrumbs active={0} />

      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        Select a delivery address
      </Typography>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        {savedAddresses.length === 0 ? (
          <Typography color="text.secondary">
            No saved addresses found.
          </Typography>
        ) : (
          <RadioGroup
            value={selectedAddressId}
            onChange={(e) => setSelectedAddressId(e.target.value)}
          >
            {savedAddresses.map((addr) => (
              <Paper key={addr._id} sx={{ p: 2, mb: 2 }}>
                <FormControlLabel
                  value={addr._id}
                  control={<Radio />}
                  sx={{ alignItems: "flex-start" }}
                  label={
                    <Box>
                      <Typography fontWeight={600}>
                        {user?.firstName} {user?.lastName}
                      </Typography>
                      <Typography fontSize={14}>
                        {addr.addressLine1}, {addr.city}, {addr.state}{" "}
                        {addr.pincode}
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            ))}
          </RadioGroup>
        )}

        <Button
          fullWidth
          variant="outlined"
          sx={{ mt: 2 }}
          onClick={() => setOpenAdd(true)}
        >
          ➕ Add new address
        </Button>
      </Paper>

      <Divider sx={{ my: 2 }} />

      <Button fullWidth variant="contained" onClick={handleContinue}>
        Deliver to this address
      </Button>

      {/* ---------------- ADD ADDRESS DIALOG WITH MAP ---------------- */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add New Delivery Address</DialogTitle>
        <DialogContent sx={{ py: 2 }}>
          {addressError && (
            <Alert severity="error" sx={{ mb: 2 }}>{addressError}</Alert>
          )}

          {/* MAP PICKER */}
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Step 1: Set your delivery location on the map
          </Typography>
          <Box key={mapKey}>
            <MapPicker
              setValue={(field, value) => {
                if (field === "location") {
                  handleLocationSelected(value);
                } else {
                  handleAddressFilled({ [field]: value });
                }
              }}
              addressFieldName="addressLine1"
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* ADDRESS DETAILS */}
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Step 2: Confirm or edit your address details
          </Typography>

          <TextField
            label="Address Line 1"
            fullWidth
            sx={{ mb: 2 }}
            value={newAddress.addressLine1}
            onChange={(e) =>
              setNewAddress({ ...newAddress, addressLine1: e.target.value })
            }
            required
          />
          <TextField
            label="Address Line 2 (optional)"
            fullWidth
            sx={{ mb: 2 }}
            value={newAddress.addressLine2}
            onChange={(e) =>
              setNewAddress({ ...newAddress, addressLine2: e.target.value })
            }
          />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
            <TextField
              label="Pincode"
              value={newAddress.pincode}
              onChange={(e) =>
                setNewAddress({ ...newAddress, pincode: e.target.value.replace(/\D/g, "") })
              }
              inputProps={{ maxLength: 6 }}
              fullWidth
              required
            />
            <TextField
              label="City"
              value={newAddress.city}
              onChange={(e) =>
                setNewAddress({ ...newAddress, city: e.target.value })
              }
              fullWidth
              required
            />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
            <TextField
              label="State"
              value={newAddress.state}
              onChange={(e) =>
                setNewAddress({ ...newAddress, state: e.target.value })
              }
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Address Type</InputLabel>
              <Select
                value={newAddress.addressType}
                onChange={(e) =>
                  setNewAddress({ ...newAddress, addressType: e.target.value })
                }
                label="Address Type"
              >
                <MenuItem value="Home">Home</MenuItem>
                <MenuItem value="Work">Work</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {newAddress.addressType === "Other" && (
            <TextField
              label="Specify Address Type"
              fullWidth
              sx={{ mb: 2 }}
              value={newAddress.otherType}
              onChange={(e) =>
                setNewAddress({ ...newAddress, otherType: e.target.value })
              }
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenAdd(false); setAddressError(""); }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveAddress}>
            Save Address
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}